import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NavigationService } from '../../../Layout/navigation.service';
import { RestrictData, RestrictService } from '../../../Common/Permissions/restrict.service';
import { UIRouter } from '@uirouter/angular';
import { BreadCrumb, BreadCrumbsComponent } from '../../../UI-Lib/Bread-Crumb/breadCrumbs.component';
import { CompanyService } from '../company.service';
import { AceLoaderComponent } from '../../../UI-Lib/ACE/Loader/loader.component';
import { HelpModule } from '../../../UI-Lib/Help-Tooltip/help.module';
import { ReturnSettingsEntityTypeEnum } from 'src/app/Compliance/Return/Models/enums';
import { ReturnSettingsComponent } from 'src/app/Compliance/Return/Return-Parts/Settings/Return-Settings/returnSettings.component';
import { BusyIndicatorMessageManager, BusyIndicatorModule } from 'src/app/Busy-Indicator';

@Component({
    selector: 'return-settings-company',
    templateUrl: './returnSettingsCompany.component.html',
    imports: [
        CommonModule,
        BreadCrumbsComponent,
        AceLoaderComponent,
        HelpModule,
        ReturnSettingsComponent,
        BusyIndicatorModule,
    ],
    styles: [
        `
            :host {
                display: flex;
            }
        `,
    ],
})
export class ReturnSettingsCompanyComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _router: UIRouter,
        private readonly _companyService: CompanyService,
        private readonly _navigationService: NavigationService,
        private readonly _restrictService: RestrictService,
    ) {}

    @ViewChild(ReturnSettingsComponent) returnSettingsComponent: ReturnSettingsComponent;

    breadcrumbs: BreadCrumb[] = [];

    isEditMode: boolean = false;
    editLock: boolean = false;
    busyIndicatorMessageManager = new BusyIndicatorMessageManager<string>();
    ReturnSettingsEntityTypeEnum: typeof ReturnSettingsEntityTypeEnum = ReturnSettingsEntityTypeEnum;
    companyId: number = 0;
    hasChanges: boolean = false;
    hasEditPermission: boolean = false;
    isLoading: boolean = true;

    private _destroy$: Subject<void> = new Subject();

    async ngOnInit(): Promise<void> {
        this.companyId = +this._router.globals.params['companyId'];

        const restrictionData = {
            isCompany: true,
            entityId: this.companyId,
            flag: Core.AccessRightsEnum.Write,
        } as RestrictData;

        const hasWritePermission = await this._restrictService.hasPermission(restrictionData);
        // getCompany checks the cache first, so no extra call if already loaded
        const company = await this._companyService.getCompany(this.companyId);

        this.hasEditPermission = hasWritePermission && company.canEditCompany;

        if (!this.hasEditPermission) {
            this._router.stateService.go('company', { companyId: this.companyId });
        }

        this.breadcrumbs = [{ name: company.companyName, target: 'company', options: { companyId: this.companyId } }];

        this._navigationService.globalEditMode$.pipe(takeUntil(this._destroy$)).subscribe((editMode) => {
            this.editLock = editMode;
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    async save(): Promise<void> {
        try {
            if (this.returnSettingsComponent) {
                this.busyIndicatorMessageManager.add('Saving', 'saving');
                await this.returnSettingsComponent.save();
                this.busyIndicatorMessageManager.remove('saving');
            }
            this.isEditMode = false;
        } finally {
            this.busyIndicatorMessageManager.remove('saving');
            this._navigationService.disableNavWarning();
        }
    }

    async setEditMode(): Promise<void> {
        this.isEditMode = true;
        this._navigationService.enableNavWarning('You have unsaved changes. Are you sure you want to leave this page?');
        await this.returnSettingsComponent.edit();
    }

    async cancelEdit(): Promise<void> {
        this.isEditMode = false;
        this._navigationService.disableNavWarning();
        await this.returnSettingsComponent.cancel();
    }

    onReturnSettingsInitialized() {
        console.log('here');
        this.isLoading = false;
    }

    onReturnSettingsChanged(value: boolean) {
        this.hasChanges = value;
    }
}
